const DB_NAME = "casa-italia-offline";
const DB_VERSION = 2;
const SNAPSHOTS = "snapshots";
const OUTBOX = "outbox";
const FILES = "files";

function openDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(SNAPSHOTS)) {
        database.createObjectStore(SNAPSHOTS);
      }
      if (!database.objectStoreNames.contains(OUTBOX)) {
        database.createObjectStore(OUTBOX, { keyPath: "id", autoIncrement: true });
      }
      if (!database.objectStoreNames.contains(FILES)) {
        database.createObjectStore(FILES);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function requestResult(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function withStore(name, mode, callback) {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(name, mode);
    const result = await callback(transaction.objectStore(name));
    await new Promise((resolve, reject) => {
      transaction.oncomplete = resolve;
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
    return result;
  } finally {
    database.close();
  }
}

export async function cacheDatabase(database) {
  const snapshot = {
    database,
    cachedAt: new Date().toISOString(),
  };
  await withStore(SNAPSHOTS, "readwrite", (store) =>
    requestResult(store.put(snapshot, "latest")),
  );
}

export async function readCachedDatabase() {
  const snapshot = await withStore(SNAPSHOTS, "readonly", (store) =>
    requestResult(store.get("latest")),
  );
  return snapshot?.database || null;
}

export async function enqueueSnapshot(database, metadata = {}) {
  return withStore(OUTBOX, "readwrite", (store) =>
    requestResult(
      store.add({
        database,
        metadata,
        queuedAt: new Date().toISOString(),
      }),
    ),
  );
}

export async function readLatestQueuedSnapshot() {
  const rows = await withStore(OUTBOX, "readonly", (store) =>
    requestResult(store.getAll()),
  );
  return rows.at(-1) || null;
}

export async function clearOutbox() {
  await withStore(OUTBOX, "readwrite", (store) => requestResult(store.clear()));
}

export async function persistOfflineSnapshot(database, metadata = {}) {
  await Promise.all([
    cacheDatabase(database),
    enqueueSnapshot(database, metadata),
  ]);
}

export async function putFileBlob(key, blob) {
  if (!key || !blob) return;
  await withStore(FILES, "readwrite", (store) => requestResult(store.put(blob, String(key))));
}

export async function getFileBlob(key) {
  if (!key) return null;
  return withStore(FILES, "readonly", (store) => requestResult(store.get(String(key))));
}

export async function deleteFileBlob(key) {
  if (!key) return;
  await withStore(FILES, "readwrite", (store) => requestResult(store.delete(String(key))));
}

export async function deleteFileBlobs(keys = []) {
  await Promise.all(keys.filter(Boolean).map((key) => deleteFileBlob(key)));
}
